import axiosInstance from './axiosInstance';
import { IAccountListResponse } from '../interfaces/Account/IAccountListResponse.ts';
import { ICreateAccountPayload } from '../interfaces/Account/ICreateAccountPayload.ts';
import { ICreateAccountResponse } from '../interfaces/Account/ICreateAccountResponse.ts';
import { IUpdateAccountPayload } from '../interfaces/Account/IUpdateAccountPayload.ts';
import { IAccount } from '../interfaces/Account/IAccount.ts';

class AccountService {
  private static instance: AccountService;

  private constructor() {}

  public static getInstance(): AccountService {
    if (!AccountService.instance) {
      AccountService.instance = new AccountService();
    }
    return AccountService.instance;
  }

  public async getAccounts(
    page = 1,
    per_page = 10,
    query?: string,
    carehome_id?: string,
    roles?: string,
    ignore_case?: boolean,
    orderBy?: string,
    sortOrder: 'asc' | 'desc' = 'desc',
    organisation_id?: string,
    is_active?: string
  ): Promise<IAccountListResponse> {
    try {
      const response = await axiosInstance.get<IAccountListResponse>(
        '/accounts',
        {
          params: {
            page,
            per_page,
            query,
            carehome_id,
            roles,
            ignore_case,
            orderBy,
            sortOrder,
            organisation_id,
            is_active,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to fetch accounts');
    }
  }

  public async getAccount(accountId: string): Promise<IAccount> {
    try {
      const response = await axiosInstance.get<IAccount>(`/accounts/${accountId}`);
      return response.data;
    } catch (error) {
      throw new Error('Failed to fetch account');
    }
  }

  public async deleteAccount(accountId: string): Promise<void> {
    try {
      await axiosInstance.delete(`/accounts/${accountId}`);
    } catch (error) {
      throw new Error('Failed to delete account');
    }
  }

  public async createAccount(
    payload: ICreateAccountPayload
  ): Promise<ICreateAccountResponse> {
    const response = await axiosInstance.post<ICreateAccountResponse>(
      '/accounts',
      payload
    );
    return response.data;
  }

  public async updateAccount(
    accountId: string,
    payload: IUpdateAccountPayload
  ): Promise<ICreateAccountResponse> {
    try {
      const response = await axiosInstance.put<ICreateAccountResponse>(
        `/accounts/${accountId}`,
        payload
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to update account');
    }
  }

  public async updateAvatar(accountId: string, file: File): Promise<void> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axiosInstance.post(`/accounts/${accountId}/avatar`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error) {
      throw new Error('Failed to update avatar');
    }
  }

  public async deleteAvatar(accountId: string): Promise<void> {
    try {
      await axiosInstance.delete(`/accounts/${accountId}/avatar`);
    } catch (error) {
      throw new Error('Failed to delete avatar');
    }
  }

  public async changePassword(accountId: string, oldPassword: string, newPassword: string): Promise<void> {
    try {
      await axiosInstance.put(`/accounts/${accountId}/password`, {
        old_password: oldPassword,
        new_password: newPassword,
      });
    } catch (error) {
      throw new Error('Failed to change password');
    }
  }

  public async initiateResetPassword(accountId: string): Promise<void> {
    try {
      await axiosInstance.post(`/accounts/${accountId}/initiate_reset_password`);
    } catch (error) {
      throw new Error('Failed to initiate reset password');
    }
  }

  /**
   * Returns the number of active caregiver accounts (e.g. logged in today)
   * filtered by optional organisation_id, carehome_id and roles.
   */
  public async getActiveAccountsCount(
    organisation_id?: string,
    carehome_id?: string,
    roles?: string,
  ): Promise<number> {
    const response = await axiosInstance.get<number>('/active_accounts_count', {
      params: {
        organisation_id,
        carehome_id,
        roles,
      },
    });
    return response.data;
  }
}

export default AccountService.getInstance();
